/**
 * NotificationDropdown component - Iteration 6 - Notifications & Sharing.
 * 
 * Dropdown panel displaying user notifications with actions.
 */

import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/useLanguage';
import { NotificationItem } from './NotificationItem';
import { LoadingSpinner } from '../common/LoadingSpinner';
import type { Notification } from '../../types/notification';
import './NotificationDropdown.css';

interface NotificationDropdownProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  notifications: Notification[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onClearAll: () => Promise<void>;
  onClose: () => void;
  onRefresh: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  anchorRef,
  notifications,
  isLoading,
  hasMore,
  onLoadMore,
  onMarkAsRead,
  onMarkAllAsRead,
  onClearAll,
  onClose,
  onRefresh,
}) => {
  const { t } = useLanguage();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Position under anchor and keep within viewport (mobile-safe)
  useLayoutEffect(() => {
    const el = dropdownRef.current;
    const anchor = anchorRef.current;
    if (!el || !anchor) return;

    const margin = 8;
    const panelMaxWidth = 380;
    /** Match header mobile tweaks (components.css ~768px) */
    const fullWidthBreakpoint = 768;

    const updatePosition = () => {
      const anchorRect = anchor.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const fullWidth = vw <= fullWidthBreakpoint;

      el.classList.toggle('notification-dropdown--full-width', fullWidth);

      const top = anchorRect.bottom + (fullWidth ? 0 : margin);
      const maxH = Math.min(480, vh - top - (fullWidth ? 0 : margin));

      el.style.position = 'fixed';
      el.style.top = `${top}px`;
      el.style.right = 'auto';

      if (fullWidth) {
        el.style.left = '0';
        el.style.width = '100%';
      } else {
        const panelW = Math.min(panelMaxWidth, vw - 2 * margin);
        let left = anchorRect.right - panelW;
        left = Math.max(margin, Math.min(left, vw - panelW - margin));
        el.style.left = `${left}px`;
        el.style.width = `${panelW}px`;
      }

      el.style.maxHeight = `${maxH}px`;
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef]);

  // Close on outside press; exclude anchor so bell toggle still works
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [anchorRef, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Infinite scroll
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = list;
      if (scrollHeight - scrollTop - clientHeight < 50 && !isLoading && hasMore) {
        onLoadMore();
      }
    };

    list.addEventListener('scroll', handleScroll);
    return () => list.removeEventListener('scroll', handleScroll);
  }, [isLoading, hasMore, onLoadMore]);

  // Load initial notifications when opened
  useEffect(() => {
    if (notifications.length === 0) {
      onRefresh();
    }
  }, []);

  const hasUnread = notifications.some(n => !n.read);

  const panel = (
    <div ref={dropdownRef} className="notification-dropdown">
      <div className="notification-dropdown-header">
        <h3>{t('header.notifications')}</h3>
        <div className="notification-dropdown-actions">
          {hasUnread && (
            <button 
              className="mark-all-read-btn"
              onClick={onMarkAllAsRead}
              title={t('notifications.markAllReadTitle')}
            >
              {t('notifications.markAllRead')}
            </button>
          )}
          {notifications.length > 0 && (
            <button 
              className="clear-all-btn"
              onClick={onClearAll}
              title={t('notifications.clearAllTitle')}
            >
              {t('notifications.clearAll')}
            </button>
          )}
          {notifications.length > 0 && (
            <button 
              className="view-all-link"
              onClick={() => {
                onClose();
                navigate('/notifications');
              }}
              title={t('notifications.viewAllTitle')}
            >
              {t('notifications.viewAll')}
            </button>
          )}
        </div>
      </div>

      <div ref={listRef} className="notification-dropdown-list">
        {isLoading && notifications.length === 0 ? (
          <div className="notification-loading">
            <LoadingSpinner size="small" />
            <span>{t('notifications.loading')}</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notification-empty">
            <svg 
              className="notification-empty-icon" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <p>{t('notifications.empty')}</p>
          </div>
        ) : (
          <>
            {notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={onMarkAsRead}
                onClose={onClose}
              />
            ))}
            {isLoading && (
              <div className="notification-loading-more">
                <LoadingSpinner size="small" />
              </div>
            )}
            {!hasMore && notifications.length > 0 && (
              <div className="notification-end">
                {t('notifications.noMore')}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
};

export default NotificationDropdown;
